import React, { useState, useEffect, useRef } from "react";
import {
  Marker,
  Tooltip,
  MapContainer,
  TileLayer,
  useMap,
  Polyline,
} from "react-leaflet";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Divider,
  CircularProgress,
  Paper,
} from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "../../services/axios";
import { useNavigate } from "react-router-dom";
import { categoryColors, getCategoryIcon } from "./mapUtils";
import PolygonLabels from "./PolygonLabels";

/* =========================
   🔥 FIX AUTO SIZE MAP
========================= */

const MapAutoFix = ({ destinos }) => {
  const map = useMap();

  useEffect(() => {
    const resizeMap = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 200);
    };

    resizeMap();
    window.addEventListener("resize", resizeMap);
    window.addEventListener("orientationchange", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);
      window.removeEventListener("orientationchange", resizeMap);
    };
  }, [map]);

  useEffect(() => {
    if (!destinos || destinos.length === 0) return;

    const bounds = L.latLngBounds(
      destinos.map((d) => [
        parseFloat(d.latitud),
        parseFloat(d.longitud),
      ])
    );

    map.fitBounds(bounds, { padding: [40, 40] });
  }, [destinos, map]);

  return null;
};

/* ========================= */

function MapCat() {
  const [destinos, setDestinos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("");
  const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState("");
  const [distritoSeleccionado, setDistritoSeleccionado] = useState("");
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const markerRefs = useRef({});

  /* =========================
     🔹 CARGA DE DATOS
  ========================= */

  useEffect(() => {
    const fetchDestinos = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/destinos");
        const data = response.data;

        setDestinos(data);
        setCategorias([...new Set(data.map((d) => d.categoria))]);
        setDistritos([...new Set(data.map((d) => d.ciudad))]);
      } catch (error) {
        console.error("Error cargando destinos", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinos();
  }, []);

  useEffect(() => {
    if (categoriaSeleccionada) {
      const subs = [
        ...new Set(
          destinos
            .filter((d) => d.categoria === categoriaSeleccionada)
            .map((d) => d.subcategoria)
        ),
      ];
      setSubcategorias(subs);
    } else {
      setSubcategorias([]);
      setSubcategoriaSeleccionada("");
    }
  }, [categoriaSeleccionada, destinos]);

  /* =========================
     🔹 FILTRO
  ========================= */

  const filteredDestinos = destinos.filter(
    (destino) =>
      (!categoriaSeleccionada ||
        destino.categoria === categoriaSeleccionada) &&
      (!subcategoriaSeleccionada ||
        destino.subcategoria === subcategoriaSeleccionada) &&
      (!distritoSeleccionado ||
        destino.ciudad === distritoSeleccionado)
  );

  /* =========================
     🔥 LÍNEAS ORDENADAS
  ========================= */

  const groupedBySubcategory = {};

  if (categoriaSeleccionada === "Circuitos Turísticos") {
    const sortedDestinos = [...filteredDestinos].sort((a, b) => {
      if (a.orden && b.orden) return a.orden - b.orden;
      return a.id - b.id;
    });

    sortedDestinos.forEach((destino) => {
      const sub = destino.subcategoria || "Sin Subcategoría";

      if (!groupedBySubcategory[sub]) {
        groupedBySubcategory[sub] = [];
      }

      groupedBySubcategory[sub].push([
        parseFloat(destino.latitud),
        parseFloat(destino.longitud),
      ]);
    });
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: "100dvh",
        gap: 2,
        p: 2,
      }}
    >
      {/* ================= PANEL ================= */}
      <Paper
        elevation={4}
        sx={{
          width: { xs: "100%", md: "340px" },
          flexShrink: 0,
          p: 2,
          overflowY: "auto",
          borderRadius: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", mb: 2, color: "#1976d2" }}
        >
          🌍 Filtros
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Categoría</InputLabel>
          <Select
            value={categoriaSeleccionada}
            label="Categoría"
            onChange={(e) => {
              setCategoriaSeleccionada(e.target.value);
              setSubcategoriaSeleccionada("");
            }}
          >
            <MenuItem value="">
              <em>Todas</em>
            </MenuItem>
            {categorias.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          disabled={!categoriaSeleccionada}
        >
          <InputLabel>Subcategoría</InputLabel>
          <Select
            value={subcategoriaSeleccionada}
            label="Subcategoría"
            onChange={(e) =>
              setSubcategoriaSeleccionada(e.target.value)
            }
          >
            <MenuItem value="">
              <em>Todas</em>
            </MenuItem>
            {subcategorias.map((sub) => (
              <MenuItem key={sub} value={sub}>
                {sub}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Distrito</InputLabel>
          <Select
            value={distritoSeleccionado}
            label="Distrito"
            onChange={(e) =>
              setDistritoSeleccionado(e.target.value)
            }
          >
            <MenuItem value="">
              <em>Todos</em>
            </MenuItem>
            {distritos.map((dist) => (
              <MenuItem key={dist} value={dist}>
                {dist}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          📌 Categorías
        </Typography>

        {Object.entries(categoryColors).map(([categoria, color]) => (
          <Box
            key={categoria}
            sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: color,
                border: "1px solid #333",
                mr: 1,
              }}
            />
            <Typography variant="body2">
              {categoria}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* ================= MAPA ================= */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: 3,
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <MapContainer
            center={[-15.4997, -70.1333]}
            zoom={14}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapAutoFix destinos={filteredDestinos} />
            <PolygonLabels destinos={filteredDestinos} />

            {categoriaSeleccionada === "Circuitos Turísticos" &&
              Object.entries(groupedBySubcategory).map(
                ([sub, coords], index) =>
                  coords.length > 1 && (
                    <Polyline
                      key={index}
                      positions={coords}
                      pathOptions={{
                        color: "#1976d2",
                        weight: 5,
                      }}
                    />
                  )
              )}

            {filteredDestinos.map((destino) => {
              const lat = parseFloat(destino.latitud);
              const lng = parseFloat(destino.longitud);
              if (isNaN(lat) || isNaN(lng)) return null;

              return (
                <Marker
                  key={destino.id}
                  position={[lat, lng]}
                  icon={getCategoryIcon(destino.categoria)}
                  eventHandlers={{
                    click: () =>
                      navigate(`/informacion/${destino.id}`),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                    <Box
                      sx={{
                        maxWidth: 260,
                        minWidth: 180,
                        p: 1,
                        overflow: "hidden",
                      }}
                    >
                      {destino.foto && (
                        <Box
                          component="img"
                          src={destino.foto}
                          alt={destino.nombre}
                          sx={{
                            width: "100%",
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                      )}

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          fontSize: 14,
                          lineHeight: 1.4,
                          whiteSpace: "normal",       // 🔥 permite múltiples líneas
                          wordBreak: "break-word",    // 🔥 evita que se salga
                          overflowWrap: "anywhere",   // 🔥 fuerza ajuste si es muy largo
                        }}
                      >
                        {destino.nombre}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        {destino.categoria}
                      </Typography>
                    </Box>
                  </Tooltip>

                </Marker>
              );
            })}
          </MapContainer>
        )}
      </Box>
    </Box>
  );
}

export default MapCat;
